import { STATUS_BUILD } from "components/Status/types"
import {
  IUseDefaultValueProps,
  useBoolean,
  useString,
  useWindowSize,
} from "helpers/hooks"
import { getProjectBuildMiddleware } from "pages/project-build/api.services"
import {
  ProjectComponentBuildDetail,
  emptyProjectBuildDetail,
} from "pages/project-build/project-build.type"
import { useEffect, useState } from "react"
import { useParams } from "react-router-dom"
import BuildDetail from "./organisms/BuildDetail"
import BuildDraft from "./organisms/BuildDraft"
import { ReactComponent as CollapseIcon } from "assets/images/icons/collapse-icon.svg"
import { ReactComponent as ExpandIcon } from "assets/images/icons/expand-icon.svg"
import { ReactComponent as ArrowLeftIcon } from "assets/images/icons/arrow-left.svg"
import iconNoData from "assets/images/icons/icon-no-data-component.svg"
import { SkeletonBuildDetail } from "./molecules/SkeletonBuild"
import { encryptionGCMHelper } from "helpers/encryption-gcm"
import { addKeyToLocalStorageObject } from "helpers/local_storage"
import { runtimeKeyStore } from "stores/runtimeKeyStore"
import { usePageLayoutHealth } from "pages/layout/PageLayout"
import { EncryptionKeys } from "controllers/EncryptionController"

interface Props {
  isArchived: boolean | undefined
  showBuildDetailLayout: IUseDefaultValueProps
  collapse: IUseDefaultValueProps
  isForceRefresh: {
    value: boolean
    setValue: (value: boolean) => void
  }
}
const BuildDetailColumn = (props: Props) => {
  const { isPreCheckReady } = usePageLayoutHealth()
  const { isMobile } = useWindowSize()
  const { isArchived, showBuildDetailLayout, collapse, isForceRefresh } = props
  const params = useParams<{
    status: string
    idProject: string
    idProjectBuild: string
  }>()
  const [projectBuild, setProjectBuild] = useState<ProjectComponentBuildDetail>(
    emptyProjectBuildDetail
  )
  const updatedAtBuild = useString("")
  const idProjectBuildParams = params?.idProjectBuild || ""
  const isLoadingSkeletonBuild = useBoolean(false)

  useEffect(() => {
    if (isPreCheckReady) {
      return
    }
    // Clear stale build detail while health-check is running.
    setProjectBuild(emptyProjectBuildDetail)
    isLoadingSkeletonBuild.setValue(false)
  }, [isPreCheckReady])

  useEffect(() => {
    if (!isPreCheckReady) {
      return
    }
    getProjectBuild(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params?.idProjectBuild, params.idProject, isPreCheckReady])

  useEffect(() => {
    if (!isPreCheckReady || !isForceRefresh.value) return
    isForceRefresh.setValue(false)
    getProjectBuild(true)
  }, [isForceRefresh.value, isPreCheckReady])

  const getProjectBuild = async (isSkeleton = false) => {
    if (!isPreCheckReady) {
      return emptyProjectBuildDetail
    }
    if (!idProjectBuildParams) {
      setProjectBuild(emptyProjectBuildDetail)
      return emptyProjectBuildDetail
    } else {
      if (isSkeleton) {
        isLoadingSkeletonBuild.setValue(true)
      }
      const dataRes = await getProjectBuildMiddleware(idProjectBuildParams)
      if (dataRes?.id) {
        const projectKey = runtimeKeyStore.getProjectKeys()[dataRes.project_id]
        const decryptedConversationKeys = await Promise.all(
          (dataRes.conversationKeys.filter((el) => el.additionalId) || []).map(
            async ({ encryptedKey, conversationCode }) => {
              if (!encryptedKey) return { conversationCode, isDecrypted: false }

              const decrypted = await encryptionGCMHelper.decryptContext(
                projectKey,
                encryptedKey
              )
              const isDecrypted =
                typeof decrypted === "string" &&
                decrypted.length === 64 &&
                decrypted !== encryptedKey

              return { conversationCode, isDecrypted }
            }
          )
        )
        const notDecryptedConversationKeys = decryptedConversationKeys.filter(
          ({ isDecrypted }) => !isDecrypted
        )

        const decryptedBuildKey = await encryptionGCMHelper.decryptContext(
          projectKey,
          dataRes?.buildKey?.encryptedKey
        )
        if (decryptedBuildKey !== dataRes?.buildKey?.encryptedKey) {
          addKeyToLocalStorageObject(
            EncryptionKeys.buildAdditionalInfoEncryptionKeys,
            {
              [dataRes.id]: decryptedBuildKey,
            }
          )
        }

        setProjectBuild({
          ...dataRes,
          notDecryptedConversationKeys,
          decryptedBuildKey,
        })
        isLoadingSkeletonBuild.setValue(false)
        return dataRes
      }
      return emptyProjectBuildDetail
    }
  }
  const onUpdateDataBuild = (
    updatedData: Partial<ProjectComponentBuildDetail>
  ) => {
    setProjectBuild({ ...projectBuild, ...updatedData })
    updatedAtBuild.setValue(new Date().toISOString())
  }
  if (isLoadingSkeletonBuild.value) {
    return <SkeletonBuildDetail isMobile={isMobile} />
  }
  if (projectBuild.id) {
    return (
      <div className={`relative w-full`}>
        <div
          className="absolute left-[-1px] bottom-[50%] cursor-pointer hidden md:block"
          style={{
            zIndex: 10,
          }}
          onClick={() => {
            collapse.setValue(!collapse.value)
          }}
        >
          {collapse.value ? (
            <CollapseIcon className="collapse-icon" />
          ) : (
            <ExpandIcon className="collapse-icon" />
          )}
        </div>
        {projectBuild.status === STATUS_BUILD.DRAFT ? (
          <BuildDraft
            onUpdateDataEditNameBuild={onUpdateDataBuild}
            projectBuild={projectBuild}
            showBuildDetailLayout={showBuildDetailLayout}
            isArchived={isArchived}
            updatedAtBuild={updatedAtBuild.value}
            updateProjectBuild={getProjectBuild}
          />
        ) : (
          <BuildDetail
            onUpdateDataEditNameBuild={onUpdateDataBuild}
            projectBuild={projectBuild}
            showBuildDetailLayout={showBuildDetailLayout}
            isArchived={isArchived}
            updatedAtBuild={updatedAtBuild.value}
            updateProjectBuild={getProjectBuild}
            isLoadingSkeletonBuild={isLoadingSkeletonBuild.value}
          />
        )}
      </div>
    )
  }
  return (
    <div className="flex flex-1 flex-col h-full overflow-hidden">
      <div
        className="flex md:hidden p-6"
        onClick={() => {
          showBuildDetailLayout.setValue(false)
        }}
      >
        <ArrowLeftIcon />
      </div>
      <div className="bg-white flex flex-1 items-center justify-center">
        <div className="m-6">
          <img src={iconNoData} alt="icon" />
        </div>
      </div>
    </div>
  )
}
export default BuildDetailColumn
